import React, { useState } from 'react'
import { Checkin, getCheckinsByIds } from '../api/client'
import { reconstructDataFromNormalized } from '../utils/dataNormalization'
import CheckinItem from './CheckinItem'
import CheckinBodyPeekModal from './checkin/CheckinBodyPeekModal'
import ClampedBody from './checkin/ClampedBody'

type CheckinItemProps = React.ComponentProps<typeof CheckinItem>
type PassThroughProps = Omit<CheckinItemProps, 'checkin' | 'collectionItems' | 'renderBody'>

interface ChatCheckinViewProps extends PassThroughProps {
    snapshotCheckin: Checkin
    snapshotCollectionItems?: Record<string, any>
    /**
     * Hide the snapshot/live toggle. Use when we know the snapshot is current
     * (e.g. checkin was just created or edited via Genie in this session, so
     * the message row will be backfilled and snapshot == live).
     */
    suppressToggle?: boolean
    /**
     * Clip the body to three lines and offer to read it in full. Supplied by the
     * list around the card, which owns which single card is open — so one long
     * checkin can't swamp the chat, and two peeks can't be open at once.
     */
    onPeek?: () => void
    peekOpen?: boolean
    onPeekClose?: () => void
}

/**
 * Wraps CheckinItem with a snapshot/live toggle for use inside Genie chat.
 * By default displays the snapshot baked into the message. On demand, fetches
 * the current state of the checkin and swaps the display until toggled back.
 */
const ChatCheckinView: React.FC<ChatCheckinViewProps> = ({
    snapshotCheckin,
    snapshotCollectionItems = {},
    suppressToggle = false,
    onPeek,
    peekOpen = false,
    onPeekClose,
    ...rest
}) => {
    const [showLive, setShowLive] = useState(false)
    const [liveCheckin, setLiveCheckin] = useState<Checkin | null>(null)
    const [liveCollectionItems, setLiveCollectionItems] = useState<Record<string, any>>({})
    const [missing, setMissing] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const haveLive = liveCheckin !== null || missing

    const toggle = async () => {
        if (showLive) {
            setShowLive(false)
            return
        }
        if (haveLive) {
            setShowLive(true)
            return
        }
        setLoading(true)
        setError(null)
        try {
            const response = await getCheckinsByIds([snapshotCheckin.id])
            const { checkins, collectionItems } = reconstructDataFromNormalized(response)
            if (checkins.length === 0) {
                setMissing(true)
            } else {
                setLiveCheckin(checkins[0])
                setLiveCollectionItems(collectionItems)
            }
            setShowLive(true)
        } catch (err) {
            console.error('Failed to fetch latest checkin:', err)
            setError('Failed to fetch latest')
        } finally {
            setLoading(false)
        }
    }

    const isLive = showLive && haveLive
    const renderedCheckin = isLive && liveCheckin ? liveCheckin : snapshotCheckin
    const renderedCollectionItems = isLive && liveCheckin ? liveCollectionItems : snapshotCollectionItems

    const toggleLabel = loading
        ? 'Loading…'
        : isLive
            ? 'Show original'
            : 'Show latest'

    return (
        <div className="relative">
            {!suppressToggle && (
                <div className="flex justify-end items-center gap-3 mb-1 text-xs">
                    {error && <span className="text-rose-500">{error}</span>}
                    <button
                        type="button"
                        onClick={toggle}
                        disabled={loading}
                        className={`underline disabled:opacity-50 ${isLive ? 'text-amber-800 hover:text-amber-900' : 'text-gray-500 hover:text-gray-700'}`}
                        title={isLive ? 'Show the version captured in this message' : 'Fetch the latest version from the server'}
                    >
                        {toggleLabel}
                    </button>
                </div>
            )}
            {isLive && missing ? (
                <div className="text-gray-600 italic px-3 py-3">
                    This checkin has been deleted.
                </div>
            ) : (
                <CheckinItem
                    {...(rest as PassThroughProps)}
                    checkin={renderedCheckin}
                    collectionItems={renderedCollectionItems}
                    renderBody={onPeek
                        ? (body) => (
                            <ClampedBody
                                className="text-gray-700 whitespace-pre-wrap"
                                wrapperClassName="mb-3"
                                measureKey={body}
                                onPeek={onPeek}
                            >
                                {body}
                            </ClampedBody>
                        )
                        : undefined}
                />
            )}
            {/* Whichever version is on screen is the one the modal shows — peeking
                at a card toggled to "latest" must not hand back the snapshot. */}
            {onPeek && (
                <CheckinBodyPeekModal
                    open={peekOpen}
                    title={rest.formatDate(renderedCheckin.applies_to, true)}
                    onClose={onPeekClose ?? (() => {})}
                >
                    <p className="text-gray-700 whitespace-pre-wrap">{renderedCheckin.body}</p>
                </CheckinBodyPeekModal>
            )}
        </div>
    )
}

export default ChatCheckinView
